import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Controller } from 'react-hook-form'
import HintIcon from '../shared/hint-icon'
import {
    SURVEY_CATEGORY_LABELS,
    UPSALE_POTENTIAL_OPTIONS,
} from '../../constants'
import type { UpsalePotential } from '../../types'
import { Checkbox } from '@/components/ui/checkbox'

// Types
export interface SurveyFormValues {
    score_service_quality: number
    score_customer_care: number
    score_tech_support: number
    score_issue_resolution: number
    score_referral_willingness: number
    note_general: string
    note_service_quality: string
    note_customer_care: string
    note_tech_support: string
    note_issue_resolution: string
    note_referral_willingness: string
    improvement: string
    upsale_potential: UpsalePotential[]
}

// Default values
const defaultFormValues: SurveyFormValues = {
    score_service_quality: 5,
    score_customer_care: 5,
    score_tech_support: 5,
    score_issue_resolution: 5,
    score_referral_willingness: 5,
    note_general: '',
    note_service_quality: '',
    note_customer_care: '',
    note_tech_support: '',
    note_issue_resolution: '',
    note_referral_willingness: '',
    improvement: '',
    upsale_potential: [],
}

interface SurveyFormProps {
    initialValues?: Partial<SurveyFormValues>
    isSubmitting?: boolean
    submitLabel?: string
    onSubmit: (values: SurveyFormValues) => Promise<void> | void
    onCancel?: () => void
    showActions?: boolean
}

const ScoreField = ({
    label,
    value,
    onChange,
    error,
    hint,
}: {
    label: string
    value: number
    onChange: (value: number) => void
    error?: string
    hint?: string
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value, 10)
        if (!isNaN(val)) {
            onChange(Math.max(1, Math.min(10, val)))
        }
    }

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
                <Label className="text-sm font-medium flex-1 flex items-center gap-1">
                    {label}
                    <span className="text-destructive ml-1">*</span>
                    {hint && <HintIcon hint={hint} />}
                </Label>
                <Input
                    type="number"
                    min={1}
                    max={10}
                    value={value}
                    onChange={handleInputChange}
                    className="w-20 text-center"
                />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    )
}

const InlineNoteField = ({
    fieldName,
    register,
    placeholder,
}: {
    fieldName: keyof SurveyFormValues
    register: ReturnType<typeof useForm<SurveyFormValues>>['register']
    placeholder?: string
}) => (
    <Textarea
        {...register(fieldName)}
        rows={2}
        placeholder={placeholder}
        className="resize-none text-sm"
    />
)

const StandaloneNoteField = ({
    label,
    fieldName,
    register,
    error,
    hint,
    placeholder,
}: {
    label: string
    fieldName: keyof SurveyFormValues
    register: ReturnType<typeof useForm<SurveyFormValues>>['register']
    error?: string
    hint?: string
    placeholder?: string
}) => {
    const { t } = useTranslation('surveys')
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                {label}
                <span className="text-xs font-normal ml-1">
                    ({t('common.optional', 'Optional')})
                </span>
                {hint && <HintIcon hint={hint} />}
            </Label>
            <Textarea
                {...register(fieldName)}
                rows={2}
                placeholder={placeholder}
                className="resize-none text-sm"
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    )
}

const SurveyForm = ({
    initialValues,
    isSubmitting = false,
    submitLabel,
    onSubmit,
    onCancel,
    showActions = true,
}: SurveyFormProps) => {
    const { t } = useTranslation('surveys')

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        control,
        formState: { errors },
    } = useForm<SurveyFormValues>({
        defaultValues: {
            ...defaultFormValues,
            ...initialValues,
        },
        mode: 'onChange',
    })

    const scoreServiceQuality = watch('score_service_quality')
    const scoreCustomerCare = watch('score_customer_care')
    const scoreTechSupport = watch('score_tech_support')
    const scoreIssueResolution = watch('score_issue_resolution')
    const scoreReferralWillingness = watch('score_referral_willingness')

    const averageScore = (
        (scoreServiceQuality +
            scoreCustomerCare +
            scoreTechSupport +
            scoreIssueResolution +
            scoreReferralWillingness) /
        5
    ).toFixed(1)

    const serializedInitialValues = JSON.stringify(initialValues)
    useEffect(() => {
        if (initialValues) {
            reset({ ...defaultFormValues, ...initialValues })
        }
    }, [serializedInitialValues, reset])

    const onSubmitHandler = handleSubmit((data) => {
        onSubmit(data)
    })

    // Each score category paired with its note field
    const scoreCategories = [
        {
            key: 'score_service_quality' as const,
            label: t(SURVEY_CATEGORY_LABELS.service_quality),
            value: scoreServiceQuality,
            noteKey: 'note_service_quality' as const,
            hint: t('surveys.form.hints.score_service_quality'),
            placeholder: t('surveys.form.descriptions.score_service_quality'),
        },
        {
            key: 'score_customer_care' as const,
            label: t(SURVEY_CATEGORY_LABELS.customer_care),
            value: scoreCustomerCare,
            noteKey: 'note_customer_care' as const,
            hint: t('surveys.form.hints.score_customer_care'),
            placeholder: t('surveys.form.descriptions.score_customer_care'),
        },
        {
            key: 'score_tech_support' as const,
            label: t(SURVEY_CATEGORY_LABELS.tech_support),
            value: scoreTechSupport,
            noteKey: 'note_tech_support' as const,
            hint: t('surveys.form.hints.score_tech_support'),
            placeholder: t('surveys.form.descriptions.score_tech_support'),
        },
        {
            key: 'score_issue_resolution' as const,
            label: t(SURVEY_CATEGORY_LABELS.issue_resolution),
            value: scoreIssueResolution,
            noteKey: 'note_issue_resolution' as const,
            hint: t('surveys.form.hints.score_issue_resolution'),
            placeholder: t('surveys.form.descriptions.score_issue_resolution'),
        },
        {
            key: 'score_referral_willingness' as const,
            label: t(SURVEY_CATEGORY_LABELS.referral_willingness),
            value: scoreReferralWillingness,
            noteKey: 'note_referral_willingness' as const,
            hint: t('surveys.form.hints.score_referral_willingness'),
            placeholder: t(
                'surveys.form.descriptions.score_referral_willingness'
            ),
        },
    ]

    // Standalone notes not tied to a score category
    const standaloneNotes: {
        key: keyof SurveyFormValues
        labelKey: string
        fallback: string
        hintKey?: string
        placeholderKey?: string
    }[] = [
        {
            key: 'improvement',
            labelKey: 'surveys.form.improvement',
            fallback: 'Improvement',
            hintKey: 'surveys.form.hints.improvement',
            placeholderKey: 'surveys.form.descriptions.improvement',
        },
        {
            key: 'note_general',
            labelKey: 'surveys.form.note_general',
            fallback: 'General Note',
        },
    ]

    const notePlaceholder = t('surveys.form.note_placeholder', 'Note...')

    return (
        <form onSubmit={onSubmitHandler} className="space-y-4">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>
                            {t('surveys.form.scores_title', 'Survey Scores')}
                        </CardTitle>
                        <div className="flex items-center gap-1.5 bg-background border px-2 py-0.5 rounded-sm shadow-sm">
                            <span className="text-[10px] uppercase font-bold text-muted-foreground">
                                {t('surveys.average_score', 'Avg')}:
                            </span>
                            <span className="text-sm font-bold text-primary">
                                {averageScore}
                            </span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    {scoreCategories.map((category) => (
                        <div
                            key={category.key}
                            className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start pb-3 border-b last:border-0 last:pb-0"
                        >
                            <ScoreField
                                label={category.label}
                                value={category.value}
                                onChange={(value) =>
                                    setValue(category.key, value)
                                }
                                error={errors[category.key]?.message}
                                hint={category.hint}
                            />
                            <InlineNoteField
                                fieldName={category.noteKey}
                                register={register}
                                placeholder={category.placeholder}
                            />
                        </div>
                    ))}

                    {/* Upsale Potential */}
                    <div className="space-y-3 pt-3 border-t">
                        <div className="text-sm font-medium flex items-center gap-1">
                            {t(
                                'surveys.column.upsale_potential',
                                'Upsale Potential'
                            )}
                            <span className="text-xs font-normal text-muted-foreground ml-1">
                                ({t('common.optional', 'Optional')})
                            </span>
                            <HintIcon
                                hint={t('surveys.form.hints.upsale_potential')}
                            />
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <Controller
                                name="upsale_potential"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        {UPSALE_POTENTIAL_OPTIONS.map(
                                            (option) => (
                                                <label
                                                    key={option.value}
                                                    className="flex items-center gap-2 cursor-pointer"
                                                >
                                                    <Checkbox
                                                        checked={field.value?.includes(
                                                            option.value
                                                        )}
                                                        onCheckedChange={(
                                                            checked
                                                        ) => {
                                                            const current =
                                                                field.value ||
                                                                []
                                                            field.onChange(
                                                                checked
                                                                    ? [
                                                                          ...current,
                                                                          option.value,
                                                                      ]
                                                                    : current.filter(
                                                                          (v) =>
                                                                              v !==
                                                                              option.value
                                                                      )
                                                            )
                                                        }}
                                                    />
                                                    <span className="text-sm">
                                                        {option.label}
                                                    </span>
                                                </label>
                                            )
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium">
                        {t('surveys.form.notes_title', 'Notes & Improvement')}
                        <span className="text-xs font-normal text-muted-foreground ml-1">
                            ({t('common.optional', 'Optional')})
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {standaloneNotes.map((field) => (
                        <StandaloneNoteField
                            key={field.key}
                            label={t(field.labelKey, field.fallback)}
                            fieldName={field.key}
                            register={register}
                            error={
                                errors[field.key]
                                    ? String(errors[field.key]?.message)
                                    : undefined
                            }
                            hint={field.hintKey ? t(field.hintKey) : undefined}
                            placeholder={
                                field.placeholderKey
                                    ? t(field.placeholderKey)
                                    : notePlaceholder
                            }
                        />
                    ))}
                </CardContent>
            </Card>

            {showActions && (
                <div className="flex items-center justify-end gap-3">
                    {onCancel && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {t('common.cancel', 'Cancel')}
                        </Button>
                    )}
                    <Button
                        type="button"
                        disabled={isSubmitting}
                        onClick={onSubmitHandler}
                    >
                        {isSubmitting
                            ? t('common.saving', 'Saving...')
                            : submitLabel || t('common.save', 'Save')}
                    </Button>
                </div>
            )}
        </form>
    )
}

export default SurveyForm
